import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  Stack,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  IconButton
} from '@mui/material';
import {
  Mic as MicIcon,
  Stop as StopIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  Send as SendIcon
} from '@mui/icons-material';
import VolumeVisualizer from './VolumeVisualizer';
import axiosInstance from '../config/axios';
import ResultadoAnalisis from './ResultadoAnalisis';

const formatearTextoAnalisis = (texto) => {
  // Remover espacios en blanco extras y normalizar saltos de línea
  return texto.replace(/\r\n/g, '\n')
             .replace(/\n\s*\n/g, '\n\n')
             .trim();
};

const extraerDatosAnalisis = (analisisTexto) => {
  // Formatear el texto para asegurar consistencia
  const textoFormateado = formatearTextoAnalisis(analisisTexto);

  // Extraer cada sección con expresiones regulares mejoradas
  const nivelUrgenciaMatch = textoFormateado.match(/NIVEL DE URGENCIA:[\s\n]*(ESI-[1-5])/i);
  
  // Validar que se encontró un nivel de urgencia
  if (!nivelUrgenciaMatch) {
    throw new Error('No se pudo determinar el nivel de urgencia del análisis');
  }

  // Extraer el resto de la información
  const diagnosticoPreliminarMatch = textoFormateado.match(/DIAGNÓSTICO PRELIMINAR:[\s\n]*([\s\S]*?)(?=\n\n|$)/i);
  const sintomasMatch = textoFormateado.match(/SÍNTOMAS IDENTIFICADOS:[\s\n]*([\s\S]*?)(?=\n\n|$)/i);
  const signosAlarmaMatch = textoFormateado.match(/SIGNOS DE ALARMA:[\s\n]*([\s\S]*?)(?=\n\n|$)/i);
  const recomendacionesMatch = textoFormateado.match(/RECOMENDACIONES:[\s\n]*([\s\S]*?)(?=\n\n|$)/i);
  const centroMatch = textoFormateado.match(/CENTRO RECOMENDADO:[\s\n]*([\s\S]*?)(?=\n\n|$)/i);

  // Procesar cada campo con manejo de errores mejorado
  const procesarLista = (texto) => {
    if (!texto) return [];
    return texto
      .split('\n')
      .map(s => s.trim())
      .filter(s => s.startsWith('-'))
      .map(s => s.substring(1).trim())
      .filter(Boolean);
  };

  // Obtener y validar el nivel de urgencia
  const nivelUrgencia = nivelUrgenciaMatch[1].trim().toUpperCase();
  if (!/^ESI-[1-5]$/.test(nivelUrgencia)) {
    throw new Error(`Nivel de urgencia inválido: ${nivelUrgencia}`);
  }

  return {
    nivelUrgencia,
    diagnosticoPreliminar: diagnosticoPreliminarMatch ? diagnosticoPreliminarMatch[1].trim() : '',
    sintomas: sintomasMatch ? procesarLista(sintomasMatch[1]) : [],
    signosAlarma: signosAlarmaMatch ? procesarLista(signosAlarmaMatch[1]) : [],
    recomendaciones: recomendacionesMatch ? procesarLista(recomendacionesMatch[1]) : [],
    centroRecomendado: centroMatch ? centroMatch[1].trim() : ''
  };
};

const AudioRecorder = () => {
  // Estados
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progreso, setProgreso] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [analisis, setAnalisis] = useState(null);
  const [stream, setStream] = useState(null);
  const [usuarioId, setUsuarioId] = useState(null);
  const [perfilCompleto, setPerfilCompleto] = useState(false);

  // Referencias
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(new Audio());

  // Pasos del proceso
  const steps = ['Grabar síntomas', 'Verificar audio', 'Ver análisis'];

  useEffect(() => {
    // Obtener el ID del usuario del token
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUsuarioId(payload.usuarioId);
        
        // Verificar el perfil del usuario
        const verificarPerfil = async () => {
          try {
            const response = await axiosInstance.get(`/usuarios/${payload.usuarioId}`);
            const usuario = response.data;
            
            // Verificar que todos los campos requeridos estén presentes
            const perfilEstaCompleto = !!(
              usuario.telefonos?.movil && 
              usuario.grupoSanguineo && 
              usuario.fechaNacimiento && 
              usuario.genero && 
              usuario.previsionSalud?.tipo && 
              usuario.contactoEmergencia?.nombre && 
              usuario.contactoEmergencia?.telefono
            );
            
            setPerfilCompleto(perfilEstaCompleto);
            
            if (!perfilEstaCompleto) {
              setError('Para realizar un análisis, primero debe completar su perfil con: teléfono móvil, grupo sanguíneo, fecha de nacimiento, género, previsión de salud y contacto de emergencia.');
              // Redirigir al perfil después de 5 segundos
              setTimeout(() => {
                window.location.href = '/perfil';
              }, 5000);
            }
          } catch (error) {
            console.error('Error al verificar perfil:', error);
            setError('Error al verificar el perfil del usuario');
          }
        };
        
        verificarPerfil();
      } catch (error) {
        console.error('Error al decodificar el token:', error);
        setError('Error al identificar al usuario');
      }
    }
  }, []);

  // Iniciar grabación
  const handleStartRecording = async () => {
    if (!perfilCompleto) {
      setError('Para realizar un análisis, primero debe completar su perfil con: teléfono móvil, grupo sanguíneo, fecha de nacimiento, género, previsión de salud y contacto de emergencia.');
      // Redirigir al perfil después de 5 segundos
      setTimeout(() => {
        window.location.href = '/perfil';
      }, 5000);
      return;
    }

    try {
      const newStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setStream(newStream);
      
      // Determinar el formato de audio según el navegador
      const mimeType = 'audio/mp4';
      const options = { 
        mimeType,
        audioBitsPerSecond: 128000
      };
      
      const newMediaRecorder = new MediaRecorder(newStream, options);
      mediaRecorderRef.current = newMediaRecorder;

      newMediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      newMediaRecorder.onstop = () => {
        if (chunksRef.current.length === 0) {
          console.error('No se recibieron datos de audio');
          setError('Error: No se recibieron datos de audio');
          return;
        }
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        setActiveStep(1);
        chunksRef.current = [];
      };

      // Configurar el MediaRecorder para que genere chunks cada 250ms
      newMediaRecorder.start(250);
      setIsRecording(true);
    } catch (error) {
      console.error('Error al iniciar la grabación:', error);
      setError('Error al acceder al micrófono');
    }
  };

  // Detener grabación
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    }
  };

  // Reproducir audio
  const playAudio = () => {
    if (audioBlob) {
      audioRef.current.src = URL.createObjectURL(audioBlob);
      audioRef.current.onended = () => setIsPlaying(false);
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  // Detener reproducción
  const stopAudio = () => {
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setIsPlaying(false);
  };

  // Reiniciar grabación
  const resetRecording = () => {
    setAudioBlob(null);
    setIsPlaying(false);
    setError(null);
    setActiveStep(0);
    setAnalisis(null);
    chunksRef.current = [];
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
  };

  // Analizar audio
  const handleAnalyzeAudio = async () => {
    if (!audioBlob) {
      setError('No hay audio para analizar');
      return;
    }

    if (!usuarioId) {
      setError('No se pudo identificar al paciente');
      return;
    }

    setLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append('audio', audioBlob);

    try {
      const response = await axiosInstance.post('/audio/analizar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = (progressEvent.loaded / progressEvent.total) * 100;
          setProgreso(Math.round(progress));
        }
      });

      if (response.data && response.data.analisis) {
        // Extraer y procesar la información del análisis
        const datosAnalisis = extraerDatosAnalisis(response.data.analisis);

        // Asegurarnos de que el resultado tenga la estructura correcta y consistente
        const resultadoFormateado = {
          analisis: response.data.analisis,
          transcripcion: response.data.transcripcion || '',
          texto: response.data.transcripcion || '',
          ...datosAnalisis
        };

        setAnalisis(resultadoFormateado);
        setActiveStep(2);

        // Guardar en el historial del paciente con la información completa
        try {
          const casoData = {
            paciente: usuarioId,
            tipo: 'audio',
            resultado: resultadoFormateado.analisis,
            transcripcion: resultadoFormateado.transcripcion,
            textoExtraido: resultadoFormateado.transcripcion,
            nivelUrgencia: datosAnalisis.nivelUrgencia,
            sintomas: datosAnalisis.sintomas || [],
            signosAlarma: datosAnalisis.signosAlarma || [],
            diagnosticoPreliminar: datosAnalisis.diagnosticoPreliminar || '',
            recomendaciones: datosAnalisis.recomendaciones || [],
            centroRecomendado: datosAnalisis.centroRecomendado || '',
            estado: 'pendiente',
            fechaCreacion: new Date().toISOString()
          };

          console.log('Guardando caso:', casoData);
          
          try {
            await axiosInstance.post('/casos', casoData);
          } catch (error) {
            if (error.response?.status === 400 && error.response?.data?.error?.includes('completar su perfil')) {
              setError('Para realizar un análisis, primero debe completar su perfil con: teléfono móvil, grupo sanguíneo, fecha de nacimiento, género, previsión de salud y contacto de emergencia.');
              // Redirigir al perfil después de 5 segundos
              setTimeout(() => {
                window.location.href = '/perfil';
              }, 5000);
              return;
            }
            throw error;
          }
        } catch (historialError) {
          console.error('Error al guardar en historial:', historialError);
          setError('Error al guardar el análisis en el historial');
        }
      } else {
        throw new Error('No se recibió un análisis válido del servidor');
      }
    } catch (error) {
      console.error('Error al analizar el audio:', error);
      let mensajeError = 'Error al analizar el audio';
      
      if (error.response) {
        if (error.response.status === 413) {
          mensajeError = 'El archivo de audio es demasiado grande. Máximo 10MB.';
        } else if (error.response.status === 415) {
          mensajeError = 'Formato de audio no soportado. Use MP3, MP4, WAV, o M4A.';
        } else if (error.response.status === 401) {
          mensajeError = 'Sesión expirada. Por favor, vuelva a iniciar sesión.';
        } else if (error.response.data?.error) {
          mensajeError = error.response.data.error;
        }
      } else if (error.message) {
        mensajeError = error.message;
      }
      
      setError(mensajeError);
    } finally {
      setLoading(false);
      setProgreso(0);
    }
  };

  // Renderizar verificación de audio
  const renderVerificacion = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Verificar Grabación
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <IconButton
          onClick={isPlaying ? stopAudio : playAudio}
          color="primary"
        >
          {isPlaying ? <StopIcon /> : <PlayArrowIcon />}
        </IconButton>
        <IconButton onClick={resetRecording} color="secondary">
          <RefreshIcon />
        </IconButton>
        <Button
          variant="contained"
          onClick={handleAnalyzeAudio}
          disabled={loading}
          startIcon={<SendIcon />}
        >
          {loading ? 'ANALIZANDO...' : 'ANALIZAR AUDIO'}
        </Button>
      </Stack>
    </Box>
  );

  // Renderizar análisis
  const renderAnalisis = () => {
    if (!analisis) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <ResultadoAnalisis resultado={analisis} tipo="audio" />
        <Button
          variant="outlined"
          onClick={resetRecording}
          sx={{ mt: 2 }}
          startIcon={<RefreshIcon />}
        >
          Nueva Grabación
        </Button>
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Análisis de Síntomas por Voz
      </Typography>
      
      <Typography variant="body1" gutterBottom align="center" sx={{ mb: 4 }}>
        Describe tus síntomas y obtén un análisis preliminar mediante IA.
      </Typography>

      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((label, index) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
            <StepContent>
              {index === 0 && (
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="contained"
                    onClick={isRecording ? stopRecording : handleStartRecording}
                    startIcon={isRecording ? <StopIcon /> : <MicIcon />}
                    color={isRecording ? "secondary" : "primary"}
                    disabled={!perfilCompleto}
                    sx={{
                      background: isRecording ? '#4A90E2' : undefined,
                      '&:hover': {
                        background: isRecording ? '#357ABD' : undefined,
                      },
                    }}
                  >
                    {isRecording ? 'DETENER GRABACIÓN' : 'INICIAR GRABACIÓN'}
                  </Button>
                  {isRecording && <VolumeVisualizer stream={stream} />}
                </Box>
              )}
              {index === 1 && audioBlob && renderVerificacion()}
              {index === 2 && renderAnalisis()}
            </StepContent>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ width: '100%', mt: 2 }}>
          <LinearProgress variant="determinate" value={progreso} />
          <Typography variant="body2" color="text.secondary" align="right">
            {progreso}%
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AudioRecorder; 